import type { ClipboardEvent } from 'react';
import type { TableBlock } from '@blogforge/shared';
import type { DragPath } from '../../types';
import { useEditorStore } from '../../stores/editorStore';

// ─────────────────────────────────────────────────────────────────
// Inspector for the table block. Without this the block could render but
// never be filled in — `attrs` is `{}` on blocks reaching the store (see
// the long note in TableRenderer: nothing zod-parses blocks on the way in,
// so no `.default()` applies and even `headers` may be missing).
//
// THE RECTANGLE INVARIANT
//   The schema tolerates jagged rows ("renderers pad short rows"), but a
//   grid editor cannot: an input for a cell that does not exist is a bug
//   waiting to happen. So every structural write here goes through
//   `rectangle()`, which normalises the sheet to a single width `w`:
//
//     · `headers`               exactly w cells
//     · every row of `rows`     exactly w cells
//     · every row of `footerRows` exactly w cells
//     · `align`                 exactly w entries ('left' when unset)
//     · `columnWidths`          exactly w entries ('' = auto)
//
//   Adding a column pads every row with '' (never shifts existing data),
//   removing one drops that index from every row, and `w` is the widest row
//   found on the way in so a jagged AI-drafted table shows all of its data
//   and becomes rectangular the first time the author touches the grid.
//
//   `align`/`columnWidths` are positional, so they are kept at length w too —
//   that is what makes "column c" mean the same thing in all five arrays.
//
// TSV PASTE
//   Pasting spreadsheet text (tab + newline separated) into a cell expands
//   the grid rightward/downward from that cell instead of dumping the whole
//   block into one input. See `parseClipboard` / `applyPaste` for the exact
//   semantics; a paste with no tab and no newline is left entirely to the
//   browser so ordinary typing/pasting keeps its native caret behaviour.
// ─────────────────────────────────────────────────────────────────

type TableAttrs = TableBlock['attrs'];
type TableColors = NonNullable<TableAttrs['colors']>;
type ColorKey = keyof TableColors;
type Align = 'left' | 'center' | 'right';
type Region = 'header' | 'body' | 'footer';
type ToggleKey = 'striped' | 'bordered' | 'headerColumn' | 'stickyHeader' | 'scrollable';

const ALIGNMENTS = ['left', 'center', 'right'] as const;

interface Sheet {
  headers: string[];
  rows: string[][];
  footerRows: string[][];
  align: Align[];
  columnWidths: string[];
}

const emptyRow = (width: number): string[] => Array.from({ length: width }, () => '');

/** Copy `cells` into a freshly allocated array of exactly `width` entries. */
function padTo(cells: readonly string[], width: number): string[] {
  return Array.from({ length: width }, (_, i) => cells[i] ?? '');
}

/** Widest row across all three regions — 0 for a completely empty table. */
function gridWidth(headers: string[], rows: string[][], footerRows: string[][]): number {
  return Math.max(
    headers.length,
    ...rows.map((row) => row.length),
    ...footerRows.map((row) => row.length),
    0,
  );
}

/**
 * The rectangle invariant, in one place. Always returns NEW arrays, so
 * callers may mutate the result freely without touching the block's data.
 */
function rectangle(sheet: Sheet, width: number): Sheet {
  return {
    headers: padTo(sheet.headers, width),
    rows: sheet.rows.map((row) => padTo(row, width)),
    footerRows: sheet.footerRows.map((row) => padTo(row, width)),
    align: Array.from({ length: width }, (_, c) => sheet.align[c] ?? 'left'),
    columnWidths: Array.from({ length: width }, (_, c) => sheet.columnWidths[c] ?? ''),
  };
}

/** Drop one column index from every region (and from the positional arrays). */
function withoutColumn(sheet: Sheet, index: number): Sheet {
  const drop = (cells: string[]): string[] => cells.filter((_, c) => c !== index);
  return {
    headers: drop(sheet.headers),
    rows: sheet.rows.map(drop),
    footerRows: sheet.footerRows.map(drop),
    align: sheet.align.filter((_, c) => c !== index),
    columnWidths: sheet.columnWidths.filter((_, c) => c !== index),
  };
}

/**
 * Split clipboard text into a matrix.
 *
 * Returns `null` for a plain single-cell paste (no tab, no newline) so the
 * caller can step aside and let the browser handle it — native paste keeps the
 * caret, the selection and undo working, and reimplementing that for the
 * common case buys nothing.
 *
 * Trailing newline: spreadsheets append one to a copied range; it is not an
 * extra row, so a single trailing empty line is dropped. Interior blank lines
 * ARE kept — they are real empty rows in the copied range.
 */
function parseClipboard(text: string): string[][] | null {
  if (!text.includes('\t') && !text.includes('\n')) return null;
  const lines = text.replace(/\r\n?/g, '\n').split('\n');
  if (lines.length > 1 && lines[lines.length - 1] === '') lines.pop();
  return lines.map((line) => line.split('\t'));
}

/**
 * Write a pasted matrix with its top-left corner at (region, rowIndex, colIndex).
 *
 * Semantics, kept deliberately small and spreadsheet-like:
 *  · The grid GROWS to fit: columns are appended on the right until the widest
 *    pasted row fits, and rows are appended at the bottom of the region being
 *    pasted into until the matrix fits. Nothing is ever shifted sideways.
 *  · Existing cells inside the pasted rectangle are overwritten — that is what
 *    paste means, and it is why the header row is not special-cased below.
 *  · The header row is simply row 0 of the sheet: pasting 3 rows starting in a
 *    header cell fills the header and then spills into body rows 0 and 1.
 *  · The result is re-rectangled, so the rectangle invariant survives.
 */
function applyPaste(
  sheet: Sheet,
  region: Region,
  rowIndex: number,
  colIndex: number,
  matrix: string[][],
): Sheet {
  const widestPasted = matrix.reduce((max, row) => Math.max(max, row.length), 0);
  const width = Math.max(
    gridWidth(sheet.headers, sheet.rows, sheet.footerRows),
    colIndex + widestPasted,
  );
  const next = rectangle(sheet, width);

  const writeBody = (row: number, col: number, value: string): void => {
    while (next.rows.length <= row) next.rows.push(emptyRow(width));
    const target = next.rows[row];
    if (target) target[col] = value;
  };

  matrix.forEach((pastedRow, r) => {
    const absoluteRow = rowIndex + r;
    pastedRow.forEach((value, c) => {
      const col = colIndex + c;
      if (col >= width) return; // cannot happen: width already covers the paste
      if (region === 'header') {
        if (absoluteRow === 0) next.headers[col] = value;
        else writeBody(absoluteRow - 1, col, value);
      } else if (region === 'body') {
        writeBody(absoluteRow, col, value);
      } else {
        while (next.footerRows.length <= absoluteRow) next.footerRows.push(emptyRow(width));
        const target = next.footerRows[absoluteRow];
        if (target) target[col] = value;
      }
    });
  });

  return next;
}

const COLOR_FIELDS: { key: ColorKey; label: string }[] = [
  { key: 'headerBg', label: 'Header background' },
  { key: 'headerText', label: 'Header text' },
  { key: 'border', label: 'Borders' },
  { key: 'stripe', label: 'Striped rows' },
  { key: 'caption', label: 'Caption text' },
];

/**
 * Boolean attrs with the value the renderer falls back to when the key is
 * absent at runtime (nothing zod-parses blocks, so `striped` really can be
 * missing — the checkbox has to show what the canvas is actually doing).
 */
const TOGGLE_FIELDS: { key: ToggleKey; label: string; hint: string; fallback: boolean }[] = [
  { key: 'striped', label: 'Striped rows', hint: 'Shade every second body row', fallback: true },
  { key: 'bordered', label: 'Bordered', hint: 'Draw the full cell grid', fallback: false },
  {
    key: 'headerColumn',
    label: 'Header column',
    hint: 'First cell of each row becomes a row header',
    fallback: false,
  },
  {
    key: 'stickyHeader',
    label: 'Sticky header',
    hint: 'Published output only — the canvas pane scrolls as a whole',
    fallback: false,
  },
  {
    key: 'scrollable',
    label: 'Scrollable',
    hint: 'Scroll the table sideways instead of squashing it',
    fallback: true,
  },
];

interface TableInspectorProps {
  block: TableBlock;
  path: DragPath;
}

export function TableInspector({ block, path }: TableInspectorProps) {
  const updateBlock = useEditorStore((s) => s.updateBlock);

  const set = (patch: Partial<TableAttrs>) => {
    updateBlock(block.id, { attrs: { ...block.attrs, ...patch } }, path);
  };

  const width = gridWidth(
    block.attrs?.headers ?? [],
    block.attrs?.rows ?? [],
    block.attrs?.footerRows ?? [],
  );

  // The sheet as the editor sees it: normalised, fresh arrays every render.
  const base = rectangle(
    {
      headers: block.attrs?.headers ?? [],
      rows: block.attrs?.rows ?? [],
      footerRows: block.attrs?.footerRows ?? [],
      align: block.attrs?.align ?? [],
      columnWidths: block.attrs?.columnWidths ?? [],
    },
    width,
  );

  const writeSheet = (sheet: Sheet) => {
    set({
      headers: sheet.headers,
      rows: sheet.rows,
      footerRows: sheet.footerRows,
      align: sheet.align,
      columnWidths: sheet.columnWidths,
    });
  };

  // `rectangle(base, width)` hands back fresh arrays, so mutating them is safe.
  const editCell = (region: Region, rowIndex: number, colIndex: number, value: string) => {
    const next = rectangle(base, width);
    if (region === 'header') {
      next.headers[colIndex] = value;
    } else {
      const target = region === 'body' ? next.rows[rowIndex] : next.footerRows[rowIndex];
      if (target) target[colIndex] = value;
    }
    writeSheet(next);
  };

  /**
   * Write a structurally changed sheet through the rectangle invariant.
   * `nextWidth` is only passed when the change itself alters the column count
   * (add/remove column); everything else keeps the current `width`.
   */
  const writeStructural = (sheet: Sheet, nextWidth = width) =>
    writeSheet(rectangle(sheet, nextWidth));

  const addColumn = () => writeStructural(base, width + 1);

  const removeColumn = (index: number) => writeStructural(withoutColumn(base, index), width - 1);

  const addRow = () => writeStructural({ ...base, rows: [...base.rows, emptyRow(width)] });

  const removeRow = (index: number) =>
    writeStructural({ ...base, rows: base.rows.filter((_, r) => r !== index) });

  const addFooterRow = () =>
    writeStructural({ ...base, footerRows: [...base.footerRows, emptyRow(width)] });

  const removeFooterRow = (index: number) =>
    writeStructural({ ...base, footerRows: base.footerRows.filter((_, r) => r !== index) });

  const setAlign = (col: number, value: Align) => {
    const next = rectangle(base, width);
    next.align[col] = value;
    writeSheet(next);
  };

  const setColumnWidth = (col: number, value: string) => {
    const next = rectangle(base, width);
    next.columnWidths[col] = value;
    writeSheet(next);
  };

  const colors: TableColors = block.attrs?.colors ?? {};
  /** Clearing writes `undefined` so the key falls back to the theme default. */
  const setColor = (key: ColorKey, value: string | undefined) => {
    const next: TableColors = { ...colors };
    next[key] = value;
    set({ colors: next });
  };

  const makePasteHandler =
    (region: Region, rowIndex: number, colIndex: number) =>
    (e: ClipboardEvent<HTMLInputElement>) => {
      const matrix = parseClipboard(e.clipboardData.getData('text/plain'));
      if (!matrix) return; // plain paste — browser handles it
      e.preventDefault();
      writeSheet(applyPaste(base, region, rowIndex, colIndex, matrix));
    };

  const inputClass = 'field-input px-2 py-1 text-xs';
  const rowLabelClass =
    'w-14 shrink-0 text-[10px] font-semibold tracking-[0.08em] text-muted uppercase';

  return (
    <div className="space-y-6">
      {/* ── Heading ─────────────────────────────────────────── */}
      <div>
        <label className="field-label">Heading (optional)</label>
        <input
          type="text"
          className="field-input"
          placeholder="2025 pricing"
          value={block.content ?? ''}
          onChange={(e) => updateBlock(block.id, { content: e.target.value }, path)}
        />
      </div>

      {/* ── Caption ─────────────────────────────────────────── */}
      <div>
        <label className="field-label">Caption (optional)</label>
        <input
          type="text"
          className="field-input"
          placeholder="Prices exclude VAT"
          value={block.attrs?.caption ?? ''}
          onChange={(e) => set({ caption: e.target.value || undefined })}
        />
      </div>

      <div className="flex items-center justify-between">
        <label className="field-label mb-0">Caption position</label>
        <select
          aria-label="Caption position"
          className="field-input"
          value={block.attrs?.captionPosition ?? 'top'}
          onChange={(e) => set({ captionPosition: e.target.value as 'top' | 'bottom' })}
        >
          <option value="top">Top</option>
          <option value="bottom">Bottom</option>
        </select>
      </div>

      {/* ── Grid ────────────────────────────────────────────── */}
      <div className="border-line space-y-3 border-t pt-4">
        <div className="flex items-center justify-between">
          <label className="field-label mb-0 font-medium">Grid</label>
          <span className="text-muted text-[10px]">
            {width} column{width === 1 ? '' : 's'} × {base.rows.length} row
            {base.rows.length === 1 ? '' : 's'}
          </span>
        </div>

        {width === 0 ? (
          <div className="border-line bg-bg-soft text-muted rounded-lg border border-dashed p-5 text-center text-sm">
            <p>No columns yet</p>
            <button
              type="button"
              className="btn-primary mt-3 px-3 py-1.5 text-xs"
              onClick={addColumn}
            >
              Add column
            </button>
          </div>
        ) : (
          <div className="border-line overflow-x-auto rounded-lg border p-3">
            <div className="min-w-max space-y-2">
              {/* Header row */}
              <div className="flex items-center gap-2">
                <span className={rowLabelClass}>Header</span>
                {base.headers.map((cell, c) => (
                  <input
                    key={c}
                    type="text"
                    aria-label={`Header column ${c + 1}`}
                    placeholder={`Column ${c + 1}`}
                    className={`${inputClass} w-36`}
                    value={cell}
                    onChange={(e) => editCell('header', 0, c, e.target.value)}
                    onPaste={makePasteHandler('header', 0, c)}
                  />
                ))}
                <span className="w-6 shrink-0" />
              </div>

              {/* Body rows */}
              {base.rows.map((row, r) => (
                <div key={r} className="flex items-center gap-2">
                  <span className={rowLabelClass}>Row {r + 1}</span>
                  {row.map((cell, c) => (
                    <input
                      key={c}
                      type="text"
                      aria-label={`Row ${r + 1} column ${c + 1}`}
                      className={`${inputClass} w-36`}
                      value={cell}
                      onChange={(e) => editCell('body', r, c, e.target.value)}
                      onPaste={makePasteHandler('body', r, c)}
                    />
                  ))}
                  <button
                    type="button"
                    aria-label={`Remove row ${r + 1}`}
                    title="Remove row"
                    className="btn-ghost w-6 shrink-0 px-0 py-1 text-xs"
                    onClick={() => removeRow(r)}
                  >
                    ×
                  </button>
                </div>
              ))}

              {/* Footer rows */}
              {base.footerRows.map((row, r) => (
                <div key={r} className="flex items-center gap-2">
                  <span className={rowLabelClass}>Foot {r + 1}</span>
                  {row.map((cell, c) => (
                    <input
                      key={c}
                      type="text"
                      aria-label={`Footer row ${r + 1} column ${c + 1}`}
                      className={`${inputClass} w-36`}
                      value={cell}
                      onChange={(e) => editCell('footer', r, c, e.target.value)}
                      onPaste={makePasteHandler('footer', r, c)}
                    />
                  ))}
                  <button
                    type="button"
                    aria-label={`Remove footer row ${r + 1}`}
                    title="Remove footer row"
                    className="btn-ghost w-6 shrink-0 px-0 py-1 text-xs"
                    onClick={() => removeFooterRow(r)}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          <button type="button" className="btn-primary px-3 py-1.5 text-xs" onClick={addColumn}>
            Add column
          </button>
          <button
            type="button"
            className="btn-ghost px-3 py-1.5 text-xs"
            disabled={width <= 1}
            onClick={() => removeColumn(width - 1)}
          >
            Remove last column
          </button>
          <button
            type="button"
            className="btn-ghost px-3 py-1.5 text-xs"
            disabled={width === 0}
            onClick={addRow}
          >
            Add row
          </button>
          <button
            type="button"
            className="btn-ghost px-3 py-1.5 text-xs"
            disabled={width === 0}
            onClick={addFooterRow}
          >
            Add footer row
          </button>
        </div>

        <p className="text-muted text-xs">
          Cells accept inline HTML (<span className="font-mono">&lt;strong&gt;</span>,{' '}
          <span className="font-mono">&lt;a&gt;</span>). Paste tab-separated spreadsheet text into a
          cell to fill the grid from there.
        </p>
      </div>

      {/* ── Per-column strip ────────────────────────────────── */}
      {width > 0 && (
        <div className="border-line space-y-3 border-t pt-4">
          <label className="field-label font-medium">Columns</label>
          <p className="text-muted text-xs">
            Width per column — percentages, pixels or <span className="font-mono">auto</span>.
          </p>
          {Array.from({ length: width }, (_, c) => (
            <div key={c} className="flex items-center gap-2">
              <span className={rowLabelClass}>Col {c + 1}</span>
              <select
                aria-label={`Column ${c + 1} alignment`}
                className={`${inputClass} w-24`}
                value={base.align[c] ?? 'left'}
                onChange={(e) => setAlign(c, e.target.value as Align)}
              >
                {ALIGNMENTS.map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
              <input
                type="text"
                aria-label={`Column ${c + 1} width`}
                className={`${inputClass} w-24 font-mono`}
                placeholder="auto"
                value={base.columnWidths[c] ?? ''}
                onChange={(e) => setColumnWidth(c, e.target.value)}
              />
              <button
                type="button"
                aria-label={`Remove column ${c + 1}`}
                title="Remove column"
                className="btn-ghost w-6 shrink-0 px-0 py-1 text-xs"
                disabled={width <= 1}
                onClick={() => removeColumn(c)}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* ── Layout toggles ──────────────────────────────────── */}
      <div className="border-line space-y-3 border-t pt-4">
        <label className="field-label font-medium">Layout</label>

        {TOGGLE_FIELDS.map(({ key, label, hint, fallback }) => (
          <label key={key} className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              className="mt-0.5"
              aria-label={label}
              checked={block.attrs?.[key] ?? fallback}
              onChange={(e) => {
                const patch: Partial<TableAttrs> = {};
                patch[key] = e.target.checked;
                set(patch);
              }}
            />
            <span>
              <span className="text-ink-soft">{label}</span>
              <span className="text-muted block text-[11px]">{hint}</span>
            </span>
          </label>
        ))}
      </div>

      {/* ── Density & vertical align ────────────────────────── */}
      <div className="border-line space-y-4 border-t pt-4">
        <div className="flex items-center justify-between">
          <label className="field-label mb-0">Density</label>
          <select
            aria-label="Density"
            className="field-input"
            value={block.attrs?.density ?? 'normal'}
            onChange={(e) => set({ density: e.target.value as 'compact' | 'normal' | 'roomy' })}
          >
            <option value="compact">Compact</option>
            <option value="normal">Normal</option>
            <option value="roomy">Roomy</option>
          </select>
        </div>

        <div className="flex items-center justify-between">
          <label className="field-label mb-0">Vertical align</label>
          <select
            aria-label="Vertical align"
            className="field-input"
            value={block.attrs?.verticalAlign ?? 'middle'}
            onChange={(e) => set({ verticalAlign: e.target.value as 'top' | 'middle' | 'bottom' })}
          >
            <option value="top">Top</option>
            <option value="middle">Middle</option>
            <option value="bottom">Bottom</option>
          </select>
        </div>
      </div>

      {/* ── Colour overrides ────────────────────────────────── */}
      <div className="border-line space-y-3 border-t pt-4">
        <label className="field-label font-medium">Colors</label>
        <p className="text-muted text-xs">
          Leave empty to use the theme/design tokens. Clear removes the override.
        </p>
        {COLOR_FIELDS.map(({ key, label }) => {
          const value = colors[key] ?? '';
          return (
            <div key={key} className="flex items-center gap-3">
              <input
                type="color"
                aria-label={`${label} colour`}
                className="border-line-strong bg-bg-soft h-9 w-9 shrink-0 cursor-pointer rounded-lg border p-1"
                value={/^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'}
                onChange={(e) => setColor(key, e.target.value)}
              />
              <input
                type="text"
                aria-label={label}
                className="field-input font-mono text-xs"
                placeholder={label}
                value={value}
                onChange={(e) => setColor(key, e.target.value || undefined)}
              />
              <button
                type="button"
                aria-label={`Clear ${label}`}
                title="Clear — fall back to the theme default"
                className="btn-ghost px-2 py-1 text-xs"
                disabled={!value}
                onClick={() => setColor(key, undefined)}
              >
                Clear
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
